import { exec } from 'child_process';
import { promisify } from 'util';
import { findFiles, readText } from './fs.js';
import path from 'path';

const execAsync = promisify(exec);

/**
 * Execute a dotnet command
 */
export async function dotnet(command: string, cwd?: string): Promise<string> {
  const options = cwd ? { cwd } : {};
  const { stdout } = await execAsync(`dotnet ${command}`, options);
  return stdout.trim();
}

/**
 * Check if dotnet CLI is available
 */
export async function isDotnetAvailable(): Promise<boolean> {
  try {
    await execAsync('dotnet --version');
    return true;
  } catch {
    return false;
  }
}

/**
 * Get dotnet version
 */
export async function getDotnetVersion(): Promise<string | null> {
  try {
    return await dotnet('--version');
  } catch {
    return null;
  }
}

/**
 * Find all .csproj files in a directory
 */
export async function findCsprojFiles(cwd?: string): Promise<string[]> {
  return findFiles('**/*.csproj', { cwd: cwd || process.cwd() });
}

/**
 * Check if a project has EF Core
 */
export async function hasEfCore(csprojPath: string): Promise<boolean> {
  try {
    const content = await readText(csprojPath);
    return content.includes('Microsoft.EntityFrameworkCore');
  } catch {
    return false;
  }
}

/**
 * Extract DbContext name from csproj or code files
 */
export async function findDbContextName(projectPath: string): Promise<string | null> {
  try {
    // Search for DbContext class definitions
    const csFiles = await findFiles('**/*.cs', { cwd: projectPath });

    for (const file of csFiles) {
      const content = await readText(file);
      // Look for class that extends DbContext
      const match = content.match(/class\s+(\w+)\s*:\s*(?:\w+,\s*)*DbContext/);
      if (match) {
        return match[1];
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * List EF Core migrations
 */
export async function listMigrations(
  projectPath: string,
  context?: string
): Promise<string[]> {
  try {
    const contextArg = context ? `--context ${context}` : '';
    const result = await dotnet(`ef migrations list ${contextArg}`, projectPath);

    return result.split('\n')
      .filter(line => line && !line.startsWith('Build') && !line.includes('...'))
      .map(line => line.trim());
  } catch {
    return [];
  }
}

/**
 * Get pending migrations (not applied to database)
 */
export async function getPendingMigrations(
  projectPath: string,
  context?: string
): Promise<string[]> {
  try {
    const contextArg = context ? `--context ${context}` : '';
    const result = await dotnet(`ef migrations list ${contextArg} --pending`, projectPath);

    return result.split('\n')
      .filter(line => line && !line.startsWith('Build') && !line.includes('...'))
      .map(line => line.trim());
  } catch {
    return [];
  }
}

/**
 * Build a .NET project
 */
export async function buildProject(projectPath: string): Promise<{ success: boolean; output: string }> {
  try {
    const output = await dotnet('build --no-restore', projectPath);
    return { success: true, output };
  } catch (error) {
    const err = error as { stdout?: string; stderr?: string };
    return {
      success: false,
      output: err.stdout || err.stderr || 'Build failed'
    };
  }
}

/**
 * Parse csproj XML to extract package references
 */
export async function getPackageReferences(csprojPath: string): Promise<Array<{ name: string; version: string }>> {
  try {
    const content = await readText(csprojPath);
    const packages: Array<{ name: string; version: string }> = [];

    const regex = /<PackageReference\s+Include="([^"]+)"\s+Version="([^"]+)"/g;
    let match;

    while ((match = regex.exec(content)) !== null) {
      packages.push({
        name: match[1],
        version: match[2],
      });
    }

    return packages;
  } catch {
    return [];
  }
}

/**
 * Get target framework from csproj
 */
export async function getTargetFramework(csprojPath: string): Promise<string | null> {
  try {
    const content = await readText(csprojPath);
    const match = content.match(/<TargetFramework>([^<]+)<\/TargetFramework>/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Get project name from csproj path
 */
export function getProjectName(csprojPath: string): string {
  return path.basename(csprojPath, '.csproj');
}

/**
 * Namespace configuration structure
 */
export interface NamespaceConfig {
  baseNamespace: string;
  domain: string;
  application: string;
  infrastructure: string;
  api: string;
}

/**
 * Detect namespaces from .csproj files
 * Extracts the base namespace from project naming patterns like:
 * - MyProject.Domain.csproj → base = "MyProject"
 * - Acme.Corp.Application.csproj → base = "Acme.Corp"
 *
 * Returns null if unable to detect (user should configure manually)
 */
export async function detectNamespaces(csprojFiles: string[]): Promise<NamespaceConfig | null> {
  if (csprojFiles.length === 0) {
    return null;
  }

  // Extract project names and find patterns
  const projectNames = csprojFiles.map(f => path.basename(f, '.csproj'));

  // Known layer suffixes (case-insensitive matching)
  const layerSuffixes = ['Domain', 'Application', 'Infrastructure', 'Api', 'Web', 'Core', 'Tests'];

  // Find base namespace by removing layer suffixes
  const baseNamespaces: string[] = [];

  for (const name of projectNames) {
    const parts = name.split('.');

    // Try to find where the layer suffix starts
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      if (layerSuffixes.some(s => s.toLowerCase() === part.toLowerCase())) {
        // Found a layer suffix, base namespace is everything before it
        const baseParts = parts.slice(0, i);
        if (baseParts.length > 0) {
          baseNamespaces.push(baseParts.join('.'));
        }
        break;
      }
    }
  }

  if (baseNamespaces.length === 0) {
    // Fallback: try to extract common prefix from all project names
    const commonPrefix = findCommonPrefix(projectNames);
    if (commonPrefix && commonPrefix.length > 0) {
      // Remove trailing dot if present
      const base = commonPrefix.replace(/\.$/, '');
      if (base) {
        baseNamespaces.push(base);
      }
    }
  }

  if (baseNamespaces.length === 0) {
    return null;
  }

  // Use the most common base namespace (in case of multiple patterns)
  const baseNamespace = getMostCommon(baseNamespaces) || baseNamespaces[0];

  return {
    baseNamespace,
    domain: `${baseNamespace}.Domain`,
    application: `${baseNamespace}.Application`,
    infrastructure: `${baseNamespace}.Infrastructure`,
    api: `${baseNamespace}.Api`,
  };
}

/**
 * Find common prefix among strings
 */
function findCommonPrefix(strings: string[]): string {
  if (strings.length === 0) return '';
  if (strings.length === 1) {
    // For single project, try to extract base from name like "MyProject.Api"
    const parts = strings[0].split('.');
    if (parts.length > 1) {
      return parts.slice(0, -1).join('.') + '.';
    }
    return strings[0] + '.';
  }

  let prefix = strings[0];

  for (let i = 1; i < strings.length; i++) {
    while (strings[i].indexOf(prefix) !== 0 && prefix.length > 0) {
      prefix = prefix.slice(0, -1);
    }
  }

  // Ensure we don't cut in the middle of a namespace part
  const lastDot = prefix.lastIndexOf('.');
  if (lastDot > 0 && !prefix.endsWith('.')) {
    prefix = prefix.slice(0, lastDot + 1);
  }

  return prefix;
}

/**
 * Get most common element in array
 */
function getMostCommon(arr: string[]): string | null {
  if (arr.length === 0) return null;

  const counts = new Map<string, number>();
  for (const item of arr) {
    counts.set(item, (counts.get(item) || 0) + 1);
  }

  let maxCount = 0;
  let mostCommon = arr[0];

  for (const [item, count] of counts) {
    if (count > maxCount) {
      maxCount = count;
      mostCommon = item;
    }
  }

  return mostCommon;
}
